import { Box, Button, Drawer, FormControl, Grid, MenuItem, Select, Stack, Typography } from "@mui/material";
import { DrawerFooter, DrawerHeader, DrawerContent } from "@sagecentral/core-ui";
import { useAddRelatedWorkItems, useWorkItemWorkspaces } from "hooks/work-items";
import { useRef, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { ISelectedWorkItemsGrid, SelectedWorkItemsGrid, WorkItemsSelectionGrid } from "./add-related-grid";

type AddRelatedWorkItemDrawerProps = {    
    workItemId: number;    
    draft?: boolean;
    selectedWorkspaceId?: number | undefined;
    refresh: () => void;
};

const AddRelatedWorkItemDrawer = (props: AddRelatedWorkItemDrawerProps) => {
    const {
        workItemId,
        selectedWorkspaceId,
        refresh,
        draft
    } = props;

    const [ open, setOpen ] = useState(false);

    const [ workspaces, workspacesLoading ] = useWorkItemWorkspaces(workItemId, draft);
    const [ workspaceId, setWorkspaceId ] = useState<number>(selectedWorkspaceId ?? 0);
    
    const [ add, working ] = useAddRelatedWorkItems(workItemId, draft);
    
    const apiRef = useGridApiRef();
    const selectedRef = useRef<ISelectedWorkItemsGrid | null>(null);

    const [ canSubmit, setCanSubmit ] = useState<boolean>(false);


    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleSave = async () => {
        const payload = selectedRef?.current?.selections ?? [];        
        if (payload.length === 0) return;

        const success = await add(payload, workspaceId);
        if (success) {
            refresh();
            setOpen(false);
        }
    };

    return (
        <>
            <Grid item xs={12} 
                sx={{
                    display: 'flex'
                }}
                justifyContent='flex-end'
            >
                <Button 
                    variant='contained' 
                    onClick={handleOpen}
                    aria-label="Open add related work item drawer"
                >
                    Add Related Work Item
                </Button>
            </Grid>
            <Drawer 
                open={open} 
                onClose={handleClose}
                anchor="right"
                sx={{
                    '& .MuiPaper-root': {
                        width: '80%'
                    }
                }}
            >
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}>
                    <DrawerHeader 
                        title='Add Related Work Items'
                        onClose={handleClose}
                    />
                    <DrawerContent>
                        <Stack spacing={2}>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <Typography id="workspace-select-label"><b>Workspace:</b></Typography>                            
                                <FormControl>
                                    <Select
                                        value={workspaceId}
                                        onChange={e => setWorkspaceId(+e.target.value)}
                                        sx={{
                                            minWidth: '200px'
                                        }}
                                        aria-labelledby="workspace-select-label"
                                        displayEmpty
                                        id="workspace-select"
                                        inputProps={{
                                            'aria-label': 'Select workspace',
                                            name: 'workspace-select'
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                (e.target as HTMLElement).click();
                                            }
                                        }}
                                    >
                                        <MenuItem 
                                            value={0}
                                            aria-label="Select a workspace"
                                            role="option"
                                        >
                                            <em>Select Workspace</em>
                                        </MenuItem>
                                        { workspaces.map((ws, idx) => (
                                            <MenuItem 
                                                key={idx} 
                                                value={ws.id}
                                                aria-label={`Select workspace: ${ws.name}`}
                                                role="option"
                                            >
                                                {ws.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                            <Typography variant='body1' fontWeight='600'>
                                Available Work Item instances to select from. Your selection(s) will appear in the table below this one:
                            </Typography>
                            <WorkItemsSelectionGrid 
                                workItemId={workItemId}
                                workspaceId={workspaceId}
                                apiRef={apiRef}
                                selectedRef={selectedRef}
                                draft={draft}
                            />
                            
                            <SelectedWorkItemsGrid
                                ref={selectedRef}
                                apiRef={apiRef}
                                setCanSubmit={setCanSubmit}
                            />
                        </Stack>
                    </DrawerContent>                    
                    <DrawerFooter 
                        confirmText="Finish and Submit"                                
                        onConfirm={handleSave}
                        onCancel={handleClose}
                        working={working}
                        disableConfirm={!canSubmit}
                    />
                </Box>
            </Drawer>
        </>
    )
};

export default AddRelatedWorkItemDrawer;